/**
 * Storedash Product Search
 * Blazing-fast AJAX product search with keyboard navigation
 * 
 * @package Storedash_Checkout
 * @since 3.2.0
 */

(function() {
    'use strict';

    class StoredashProductSearch {
        // Shared document click handler - one listener for all instances
        static _documentClickHandler = null;
        static _instances = new Set();

        static initSharedListeners() {
            if (StoredashProductSearch._documentClickHandler) return;
            StoredashProductSearch._documentClickHandler = (e) => {
                StoredashProductSearch._instances.forEach(instance => {
                    if (!instance.element.contains(e.target)) {
                        instance.hideResults();
                    }
                });
            };
            document.addEventListener('click', StoredashProductSearch._documentClickHandler);
        }

        static removeSharedListeners() {
            if (StoredashProductSearch._instances.size > 0) return;
            if (StoredashProductSearch._documentClickHandler) {
                document.removeEventListener('click', StoredashProductSearch._documentClickHandler);
                StoredashProductSearch._documentClickHandler = null;
            }
        }

        constructor(element) {
            this.element = element;
            this.form = element.querySelector('.storedash-search-form');
            this.input = element.querySelector('.storedash-search-input');
            this.results = element.querySelector('.storedash-search-results');
            this.searchIcon = element.querySelector('.storedash-search-icon');
            this.loadingIcon = element.querySelector('.storedash-loading-icon');
            this.clearBtn = element.querySelector('.storedash-clear-btn');

            // Settings - Product display
            this.minChars = parseInt(element.dataset.minChars) || 3;
            this.resultsLimit = parseInt(element.dataset.resultsLimit) || 10;
            this.searchIn = JSON.parse(element.dataset.searchIn || '["title"]');
            this.showImages = element.dataset.showImages === 'yes';
            this.showPrice = element.dataset.showPrice === 'yes';
            this.showSku = element.dataset.showSku === 'yes';
            this.showCategory = element.dataset.showCategory === 'yes';
            const historyEnabledValue = String(element.dataset.searchHistoryEnabled || '').toLowerCase();
            this.searchHistoryEnabled = ['yes', 'true', '1', 'on'].includes(historyEnabledValue);
            this.searchHistoryLimit = Math.max(1, Math.min(20, parseInt(element.dataset.searchHistoryLimit, 10) || 10));
            this.searchHistoryKey = 'storedash_search_history_v1';
            this.historyStorageAvailable = this.canUseLocalStorage();
            this.searchHistoryLabel = element.dataset.searchHistoryLabel || 'Recent Searches';
            this.searchHistoryIconUrl = this.sanitizeHistoryIconUrl(element.dataset.searchHistoryIconUrl || '');
            this.searchHistoryEmptyText = element.dataset.searchHistoryEmptyText || 'No recent searches yet';

            // Settings - Category display
            this.showCategories = element.dataset.showCategories === 'yes';
            this.categoryPosition = element.dataset.categoryPosition || 'above';
            this.categoryShowCount = element.dataset.categoryShowCount === 'yes';
            this.categoryShowImage = element.dataset.categoryShowImage === 'yes';

            // Responsive category limit (desktop / tablet / mobile)
            this._categoryLimitDesktop = parseInt(element.dataset.categoryLimit) || 5;
            this._categoryLimitTablet = element.dataset.categoryLimitTablet ? parseInt(element.dataset.categoryLimitTablet) : null;
            this._categoryLimitMobile = element.dataset.categoryLimitMobile ? parseInt(element.dataset.categoryLimitMobile) : null;
            this._resolveBreakpoints();
            this.categoryLimit = this._getResponsiveCategoryLimit();
            this._bindResponsiveCategoryLimit();

            // Settings - Layout (flexible two-column)
            this.layoutMode = element.dataset.layoutMode || 'single-column';
            this.categoriesHorizontal = element.dataset.categoriesHorizontal || 'left';
            this.categoriesVertical = element.dataset.categoriesVertical || 'top';
            this.productsHorizontal = element.dataset.productsHorizontal || 'right';
            this.productsVertical = element.dataset.productsVertical || 'top';

            // Settings - Search results page
            this.searchResultsUrl = element.dataset.searchResultsUrl || '';
            this.viewAllText = element.dataset.viewAllText || 'View All Results';
            this.viewAllSticky = element.dataset.viewAllSticky === 'yes';

            // Settings - Section labels
            this.categoriesLabel = element.dataset.categoriesLabel || 'Categories';
            this.productsLabel = element.dataset.productsLabel || 'Products';

            // Settings - Mobile behavior
            this.mobileMode = element.dataset.mobileMode || 'full';
            this.mobileExpand = element.dataset.mobileExpand || 'below';
            this.mobileAnimation = element.dataset.mobileAnimation || 'slide';
            this.mobileDuration = parseInt(element.dataset.mobileDuration) || 300;
            this.mobileCloseBehavior = JSON.parse(element.dataset.mobileClose || '["click_outside","close_button","escape_key"]');
            this.mobileBreakpoint = parseInt(element.dataset.mobileBreakpoint) || 767;

            // Mobile elements
            this.mobileTrigger = element.querySelector('.storedash-mobile-search-trigger');
            this.mobileWrapper = element.querySelector('.storedash-mobile-search-wrapper');
            this.mobileCloseBtn = element.querySelector('.storedash-mobile-search-close');
            this.mobileOverlay = element.querySelector('.storedash-mobile-search-overlay');

            // State
            this.debounceTimer = null;
            this.currentRequest = null;
            this.activeIndex = -1;
            this.cache = new Map();
            this.cacheMaxSize = 50;
            this.cacheTTL = 5 * 60 * 1000; // 5 minutes
            this.currentQuery = ''; // Track current search query
            this.isFirstQuery = true; // Track if this is first query in session

            // Mobile state
            this.isMobileExpanded = false;
            this.isMobileViewport = false;
            this.resizeTimer = null;
            this.scrollPosition = 0;

            // Ghost text (inline autocomplete) state
            const ghostEnabled = String(element.dataset.ghostTextEnabled || '').toLowerCase();
            this.ghostTextEnabled = ['yes', 'true', '1', 'on'].includes(ghostEnabled);
            this.ghostTextEl = null;
            this.suggestionIndex = [];
            this.suggestionIndexDirty = true;
            this.currentSuggestion = '';
            this.isComposing = false;

            // Fast endpoint (SHORTINIT) - set via PHP localization
            this.fastEndpoint = (typeof storedashSearch !== 'undefined' && storedashSearch.fastEndpoint)
                ? storedashSearch.fastEndpoint
                : null;

            // Bound handler references for cleanup
            this._boundHandleInput = this.handleInput.bind(this);
            this._boundHandleKeydown = this.handleKeydown.bind(this);
            this._boundHandleFocus = this.handleFocus.bind(this);
            this._boundClearSearch = this.clearSearch.bind(this);
            this._boundHandleSubmit = this.handleSubmit.bind(this);
            this._focusTrapHandler = null;
            this._mutationObserver = null;
            this._boundCompositionStart = null;
            this._boundCompositionEnd = null;
            this._boundGhostTouchEnd = null;

            StoredashProductSearch._instances.add(this);
            StoredashProductSearch.initSharedListeners();

            // PERF FIX: Single delegated click listener for search result items
            // Replaces per-item anonymous listeners that leaked on every render
            this.results.addEventListener('click', (e) => {
                const removeBtn = e.target.closest('.search-history-remove-btn');
                if (removeBtn && this.searchHistoryEnabled) {
                    e.preventDefault();
                    e.stopPropagation();
                    const queryToRemove = removeBtn.dataset.query || '';
                    this.removeFromHistory(queryToRemove);
                    this.showHistorySuggestions();
                    return;
                }

                const historyItem = e.target.closest('.search-history-item');
                if (historyItem && this.searchHistoryEnabled) {
                    const historyQuery = this.normalizeHistoryQuery(historyItem.dataset.query || '');
                    if (!historyQuery) return;
                    this.input.value = historyQuery;
                    this.currentQuery = historyQuery;
                    this.clearBtn.style.display = 'block';
                    this.isFirstQuery = false;
                    this.performSearch(historyQuery);
                    return;
                }

                const item = e.target.closest('.search-result-item');
                if (item?.dataset.url) {
                    this.addToHistory(this.currentQuery);
                    window.location.href = item.dataset.url;
                }
            });

            this.init();
        }

        init() {
            // Clean up previous listeners if re-initializing
            if (this.initialized) {
                this.destroy();
            }
            this.initialized = true;

            // Event listeners (using bound references for cleanup)
            this.input.addEventListener('input', this._boundHandleInput);
            this.input.addEventListener('keydown', this._boundHandleKeydown);
            this.input.addEventListener('focus', this._boundHandleFocus);
            this.clearBtn.addEventListener('click', this._boundClearSearch);
            this.form.addEventListener('submit', this._boundHandleSubmit);

            // Initialize ghost text (inline autocomplete) if enabled
            if (this.ghostTextEnabled) {
                this.initGhostText();
            }

            // Initialize mobile behavior if enabled
            if (this.mobileMode === 'icon' && this.mobileTrigger) {
                this.initMobile();
            }
        }

        /**
         * Resolve Elementor breakpoint widths from frontend config
         */
        _resolveBreakpoints() {
            const bp = window.elementorFrontendConfig?.responsive?.breakpoints || {};
            this._bpMobile = bp.mobile?.value || 767;
            this._bpTablet = bp.tablet?.value || 1024;
        }

        /**
         * Get category limit for current viewport width
         */
        _getResponsiveCategoryLimit() {
            const w = window.innerWidth;
            if (w <= this._bpMobile && this._categoryLimitMobile !== null) {
                return this._categoryLimitMobile;
            }
            if (w <= this._bpTablet && this._categoryLimitTablet !== null) {
                return this._categoryLimitTablet;
            }
            return this._categoryLimitDesktop;
        }

        /**
         * Listen for resize and update categoryLimit when breakpoint changes
         */
        _bindResponsiveCategoryLimit() {
            // Skip if no responsive values were set
            if (this._categoryLimitTablet === null && this._categoryLimitMobile === null) return;

            this._boundCategoryLimitResize = () => {
                const newLimit = this._getResponsiveCategoryLimit();
                if (newLimit !== this.categoryLimit) {
                    const oldLimit = this.categoryLimit;
                    this.categoryLimit = newLimit;
                    // If results are visible, re-render categories with new limit
                    if (this._lastResults && this.results.style.display !== 'none') {
                        this._applyCategoryLimit(oldLimit, newLimit);
                    }
                }
            };

            if (window.StoredashResize) {
                window.StoredashResize.on(this._boundCategoryLimitResize);
            } else {
                window.addEventListener('resize', this._boundCategoryLimitResize);
            }
        }

        /**
         * Return the largest category limit across all breakpoints (for server requests)
         */
        _maxCategoryLimit() {
            return Math.max(
                this._categoryLimitDesktop,
                this._categoryLimitTablet ?? 0,
                this._categoryLimitMobile ?? 0
            );
        }

        /**
         * Show/hide category items in the DOM when breakpoint changes
         */
        _applyCategoryLimit(oldLimit, newLimit) {
            const catItems = this.results.querySelectorAll('.search-result-category');
            if (!catItems.length) return;
            catItems.forEach((item, i) => {
                item.style.display = i < newLimit ? '' : 'none';
            });
        }

        /**
         * Clean up all event listeners to prevent memory leaks
         */
        destroy() {
            this.input.removeEventListener('input', this._boundHandleInput);
            this.input.removeEventListener('keydown', this._boundHandleKeydown);
            this.input.removeEventListener('focus', this._boundHandleFocus);
            this.clearBtn.removeEventListener('click', this._boundClearSearch);
            this.form.removeEventListener('submit', this._boundHandleSubmit);
            StoredashProductSearch._instances.delete(this);
            StoredashProductSearch.removeSharedListeners();

            if (this._boundResizeHandler) {
                if (window.StoredashResize) {
                    window.StoredashResize.off(this._boundResizeHandler);
                }
                window.removeEventListener('resize', this._boundResizeHandler);
            }
            if (this._boundCategoryLimitResize) {
                if (window.StoredashResize) {
                    window.StoredashResize.off(this._boundCategoryLimitResize);
                }
                window.removeEventListener('resize', this._boundCategoryLimitResize);
            }
            if (this._boundOrientationHandler) {
                window.removeEventListener('orientationchange', this._boundOrientationHandler);
            }
            if (this._boundVisibilityHandler) {
                document.removeEventListener('visibilitychange', this._boundVisibilityHandler);
            }
            if (this._mutationObserver) {
                this._mutationObserver.disconnect();
                this._mutationObserver = null;
            }

            this.disableFocusTrap();
            this.destroyGhostText();

            if (this.currentRequest) {
                this.currentRequest.abort();
                this.currentRequest = null;
            }
            clearTimeout(this.debounceTimer);
        }

        /**
         * Initialize mobile-specific behavior
         */
        initMobile() {
            // Check viewport on init
            this.checkMobileViewport();

            // Mobile trigger click
            this.mobileTrigger.addEventListener('click', () => {
                this.openMobileSearch();
            });

            // Close button click
            if (this.mobileCloseBtn && this.mobileCloseBehavior.includes('close_button')) {
                this.mobileCloseBtn.addEventListener('click', () => {
                    this.closeMobileSearch();
                });
            }

            // Overlay click (for fullscreen mode)
            if (this.mobileOverlay && this.mobileCloseBehavior.includes('click_outside')) {
                this.mobileOverlay.addEventListener('click', () => {
                    this.closeMobileSearch();
                });
            }

            // Click outside handler
            if (this.mobileCloseBehavior.includes('click_outside')) {
                document.addEventListener('click', (e) => {
                    if (this.isMobileExpanded && this.isMobileViewport) {
                        // Check if click is outside the wrapper and trigger
                        const isInsideWrapper = this.mobileWrapper && this.mobileWrapper.contains(e.target);
                        const isInsideTrigger = this.mobileTrigger && this.mobileTrigger.contains(e.target);
                        const isInsideOverlay = this.mobileOverlay && this.mobileOverlay.contains(e.target);

                        if (!isInsideWrapper && !isInsideTrigger && !isInsideOverlay) {
                            this.closeMobileSearch();
                        }
                    }
                });
            }

            // Escape key handler
            if (this.mobileCloseBehavior.includes('escape_key')) {
                document.addEventListener('keydown', (e) => {
                    if (e.key === 'Escape' && this.isMobileExpanded && this.isMobileViewport) {
                        this.closeMobileSearch();
                        this.mobileTrigger.focus();
                    }
                });
            }

            // PERF FIX: Use shared resize broadcaster if available (already debounced)
            this._boundResizeHandler = () => this.checkMobileViewport();
            if (window.StoredashResize) {
                window.StoredashResize.on(this._boundResizeHandler);
            } else {
                const origHandler = this._boundResizeHandler;
                this._boundResizeHandler = () => {
                    clearTimeout(this.resizeTimer);
                    this.resizeTimer = setTimeout(origHandler, 100);
                };
                window.addEventListener('resize', this._boundResizeHandler);
            }

            // Orientation change handler (for mobile devices)
            this._boundOrientationHandler = () => {
                setTimeout(() => {
                    this.checkMobileViewport();
                }, 150);
            };
            window.addEventListener('orientationchange', this._boundOrientationHandler);

            // Also check on visibility change (when tab becomes visible again)
            this._boundVisibilityHandler = () => {
                if (!document.hidden) {
                    this.checkMobileViewport();
                }
            };
            document.addEventListener('visibilitychange', this._boundVisibilityHandler);

            // Watch for Elementor editor device preview changes
            // When user clicks mobile/tablet/desktop icons, body class changes
            this.initElementorDeviceObserver();
        }

        /**
         * Watch for Elementor editor device class changes on body
         */
        initElementorDeviceObserver() {
            // Only needed in Elementor editor context
            if (!window.elementor && !window.elementorFrontend) {
                return;
            }

            this._mutationObserver = new MutationObserver((mutations) => {
                for (const mutation of mutations) {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                        // Body class changed, check if device mode changed
                        this.checkMobileViewport();
                        break;
                    }
                }
            });

            this._mutationObserver.observe(document.body, {
                attributes: true,
                attributeFilter: ['class']
            });
        }

        /**
         * Check if we're in mobile viewport and update visibility
         * Handles both actual viewport width AND Elementor editor preview mode
         */
        checkMobileViewport() {
            const wasMobile = this.isMobileViewport;

            // Check actual viewport width
            let isMobile = window.innerWidth <= this.mobileBreakpoint;

            // Also check for Elementor editor preview mode
            // When in Elementor editor, the body gets classes like 'elementor-device-mobile' or 'elementor-device-tablet'
            const body = document.body;
            const isElementorMobilePreview = body.classList.contains('elementor-device-mobile');
            const isElementorTabletPreview = body.classList.contains('elementor-device-tablet');

            // If Elementor is showing mobile or tablet preview, treat as mobile
            // (depending on whether tablet breakpoint is below our mobile breakpoint)
            if (isElementorMobilePreview) {
                isMobile = true;
            } else if (isElementorTabletPreview) {
                // Elementor's default tablet breakpoint is 1024px
                // If our mobile breakpoint is >= 1024, tablet preview should also be mobile mode
                // Otherwise, only mobile preview triggers mobile mode
                if (this.mobileBreakpoint >= 1024) {
                    isMobile = true;
                }
            }

            // Also check if we're in Elementor editor with desktop preview
            const isElementorDesktopPreview = body.classList.contains('elementor-device-desktop');
            if (isElementorDesktopPreview) {
                isMobile = false;
            }

            this.isMobileViewport = isMobile;

            if (this.isMobileViewport) {
                // Add mobile active class
                this.element.classList.add('storedash-mobile-active');

                // If we were expanded on desktop and switched to mobile, ensure proper state
                if (!wasMobile && !this.isMobileExpanded) {
                    // Make sure wrapper is hidden on mobile
                    if (this.mobileWrapper) {
                        this.mobileWrapper.classList.remove('is-expanded');
                        this.mobileWrapper.setAttribute('aria-hidden', 'true');
                    }
                    if (this.mobileTrigger) {
                        this.mobileTrigger.setAttribute('aria-expanded', 'false');
                    }
                }
            } else {
                // Remove mobile active class
                this.element.classList.remove('storedash-mobile-active');

                // If we were on mobile and expanded, close it
                if (wasMobile && this.isMobileExpanded) {
                    this.closeMobileSearch(true); // instant close
                }
            }
        }

        /**
         * Open mobile search with animation
         */
        openMobileSearch() {
            if (!this.isMobileViewport || this.isMobileExpanded) return;

            this.isMobileExpanded = true;

            // Store scroll position for fullscreen mode
            if (this.mobileExpand === 'fullscreen') {
                this.scrollPosition = window.pageYOffset;
                document.body.classList.add('storedash-search-overlay-open');
            }

            // Show overlay for fullscreen mode
            if (this.mobileOverlay && this.mobileExpand === 'fullscreen') {
                this.mobileOverlay.classList.add('is-visible');
            }

            // Update ARIA states
            if (this.mobileTrigger) {
                this.mobileTrigger.setAttribute('aria-expanded', 'true');
            }
            if (this.mobileWrapper) {
                this.mobileWrapper.setAttribute('aria-hidden', 'false');

                // Force reflow before adding class for animation
                this.mobileWrapper.offsetHeight;

                // Add expanded class
                this.mobileWrapper.classList.add('is-expanded');
            }

            // Enable focus trap for fullscreen mode
            if (this.mobileExpand === 'fullscreen') {
                this.enableFocusTrap();
            }

            // Focus input after animation completes
            setTimeout(() => {
                if (this.input) {
                    this.input.focus();
                }
            }, this.mobileAnimation !== 'none' ? this.mobileDuration : 0);
        }

        /**
         * Close mobile search with animation
         * @param {boolean} instant - Skip animation
         */
        closeMobileSearch(instant = false) {
            if (!this.isMobileExpanded) return;

            this.isMobileExpanded = false;

            // Disable focus trap
            this.disableFocusTrap();

            // Hide overlay
            if (this.mobileOverlay) {
                this.mobileOverlay.classList.remove('is-visible');
            }

            // Remove body scroll lock
            if (this.mobileExpand === 'fullscreen') {
                document.body.classList.remove('storedash-search-overlay-open');
                // Restore scroll position
                window.scrollTo(0, this.scrollPosition);
            }

            // Update ARIA states
            if (this.mobileTrigger) {
                this.mobileTrigger.setAttribute('aria-expanded', 'false');
            }

            if (this.mobileWrapper) {
                this.mobileWrapper.classList.remove('is-expanded');

                // Set aria-hidden after animation completes
                const duration = instant ? 0 : (this.mobileAnimation !== 'none' ? this.mobileDuration : 0);
                setTimeout(() => {
                    if (!this.isMobileExpanded && this.mobileWrapper) {
                        this.mobileWrapper.setAttribute('aria-hidden', 'true');
                    }
                }, duration);
            }

            // Hide search results
            this.hideResults();

            // Clear input
            if (this.input) {
                this.input.value = '';
                this.clearBtn.style.display = 'none';
            }

            // Reset first query state
            this.isFirstQuery = true;
        }

        /**
         * Toggle mobile search state
         */
        toggleMobileSearch() {
            if (this.isMobileExpanded) {
                this.closeMobileSearch();
            } else {
                this.openMobileSearch();
            }
        }

        canUseLocalStorage() {
            try {
                if (!window.localStorage) return false;
                const testKey = '__storedash_search_history_test__';
                window.localStorage.setItem(testKey, '1');
                window.localStorage.removeItem(testKey);
                return true;
            } catch (e) {
                return false;
            }
        }

        normalizeHistoryQuery(query) {
            return String(query || '')
                .trim()
                .replace(/\s+/g, ' ')
                .slice(0, 120);
        }

        sanitizeHistoryIconUrl(url) {
            const raw = String(url || '').trim();
            if (!raw) {
                return '';
            }

            try {
                const parsed = new URL(raw, window.location.origin);
                if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
                    return parsed.href;
                }
            } catch (e) {
                return '';
            }

            return '';
        }

        renderHistoryIcon() {
            if (this.searchHistoryIconUrl) {
                return `
                    <img
                        class="search-history-leading-icon search-history-leading-icon-custom"
                        src="${this.escapeHtml(this.searchHistoryIconUrl)}"
                        alt=""
                        decoding="async"
                    />
                `;
            }

            return `
                <span class="search-history-leading-icon search-history-leading-icon-default" aria-hidden="true">
                    <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                        <circle cx="12" cy="12" r="9"></circle>
                        <path d="M12 7v5l3 2"></path>
                    </svg>
                </span>
            `;
        }

        loadHistory() {
            if (!this.historyStorageAvailable) return [];

            try {
                const raw = window.localStorage.getItem(this.searchHistoryKey);
                if (!raw) return [];

                const parsed = JSON.parse(raw);
                if (!Array.isArray(parsed)) return [];

                const normalized = parsed
                    .filter((item) => typeof item === 'string')
                    .map((item) => this.normalizeHistoryQuery(item))
                    .filter(Boolean);

                const deduped = [];
                const seen = new Set();
                normalized.forEach((item) => {
                    const key = item.toLowerCase();
                    if (!seen.has(key)) {
                        seen.add(key);
                        deduped.push(item);
                    }
                });

                return deduped.slice(0, this.searchHistoryLimit);
            } catch (e) {
                return [];
            }
        }

        saveHistory(history) {
            if (!this.historyStorageAvailable) return;

            try {
                window.localStorage.setItem(
                    this.searchHistoryKey,
                    JSON.stringify(history.slice(0, this.searchHistoryLimit))
                );
            } catch (e) {
                // Ignore storage write failures (private mode/quota)
            }
        }

        addToHistory(query) {
            if (!this.searchHistoryEnabled) return;
            const normalized = this.normalizeHistoryQuery(query || this.input?.value || '');
            if (!normalized) return;

            const current = this.loadHistory().filter((item) => item.toLowerCase() !== normalized.toLowerCase());
            current.unshift(normalized);
            this.saveHistory(current);
            this.suggestionIndexDirty = true;
        }

        removeFromHistory(query) {
            if (!this.searchHistoryEnabled) return;
            const normalized = this.normalizeHistoryQuery(query);
            if (!normalized) return;

            const next = this.loadHistory().filter((item) => item.toLowerCase() !== normalized.toLowerCase());
            this.saveHistory(next);
        }

        showHistorySuggestions() {
            if (!this.searchHistoryEnabled) return false;

            const history = this.loadHistory();
            if (!history.length) {
                this.results.innerHTML = `
                    <div class="search-no-results search-history-empty">
                        <p>${this.searchHistoryEmptyText}</p>
                    </div>
                `;
                this.showResults();
                return false;
            }

            this.renderHistorySuggestions(history);
            return true;
        }

        renderHistorySuggestions(history) {
            const historyIcon = this.renderHistoryIcon();
            const historyItems = history.map((query) => {
                const escapedQuery = this.escapeHtml(query);
                return `
                    <div class="search-result-item search-history-item" role="option" data-query="${escapedQuery}">
                        <div class="search-history-main">
                            ${historyIcon}
                            <div class="search-result-details">
                                <div class="search-result-title">${escapedQuery}</div>
                            </div>
                        </div>
                        <button
                            type="button"
                            class="search-history-remove-btn"
                            data-query="${escapedQuery}"
                            aria-label="Remove ${escapedQuery} from search history">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                `;
            }).join('');

            this.results.innerHTML = `
                <div class="search-section search-section-history">
                    <div class="search-section-header">${this.escapeHtml(this.searchHistoryLabel)}</div>
                    ${historyItems}
                </div>
            `;
            this.activeIndex = -1;
            this.showResults();
            this.announceHistoryCount(history.length);
        }

        handleInput(e) {
            this.updateGhostText();

            const query = e.target.value.trim();

            // Show/hide clear button
            this.clearBtn.style.display = query.length > 0 ? 'block' : 'none';

            if (query.length === 0) {
                this.showHistorySuggestions();
                return;
            }

            if (query.length < this.minChars) {
                if (!this.showHistorySuggestions()) {
                    this.hideResults();
                }
                return;
            }
            
            // Debounced search
            this.debounceSearch(query);
        }

        debounceSearch(query) {
            clearTimeout(this.debounceTimer);

            if (query.length < this.minChars) {
                return;
            }

            // First valid query fires immediately for instant feel
            if (this.isFirstQuery) {
                this.isFirstQuery = false;
                this.performSearch(query);
                return;
            }

            // Subsequent queries use debounce to prevent server hammering
            this.debounceTimer = setTimeout(() => {
                this.performSearch(query);
            }, 200);
        }

        /**
         * Build FormData for admin-ajax.php requests (shared between fallback and primary paths)
         */
        buildAjaxFormData(query) {
            const formData = new FormData();
            formData.append('action', 'storedash_product_search');
            formData.append('nonce', storedashSearch.nonce);
            formData.append('query', query);
            formData.append('limit', this.resultsLimit);
            formData.append('min_chars', this.minChars);
            formData.append('category_limit', this._maxCategoryLimit());
            const searchIn = ['products'];
            if (this.showCategories) {
                searchIn.push('categories');
            }
            formData.append('search_in', JSON.stringify(searchIn));
            return formData;
        }

        /**
         * Get from cache with TTL check
         */
        cacheGet(key) {
            const entry = this.cache.get(key);
            if (!entry) return null;
            if (Date.now() - entry.timestamp > this.cacheTTL) {
                this.cache.delete(key);
                return null;
            }
            return entry.data;
        }

        /**
         * Set cache with TTL and LRU eviction
         */
        cacheSet(key, data) {
            // LRU eviction: delete oldest entry if at max size
            if (this.cache.size >= this.cacheMaxSize) {
                const firstKey = this.cache.keys().next().value;
                this.cache.delete(firstKey);
            }
            this.cache.set(key, { data, timestamp: Date.now() });
        }

        async performSearch(query) {
            // Guard against missing localization
            if (typeof storedashSearch === 'undefined') {
                console.warn('Storedash: Search configuration not loaded');
                this.hideLoading();
                return;
            }

            // Track current query for redirects
            this.currentQuery = query;

            // Check cache first (with TTL)
            // Include limits in key so changing result count doesn't serve stale data
            const cacheKey = `${query.toLowerCase()}_${this.resultsLimit}_${this._maxCategoryLimit()}`;
            const cached = this.cacheGet(cacheKey);
            if (cached) {
                this.displayResults(cached, query);
                return;
            }

            // Cancel previous request
            if (this.currentRequest) {
                this.currentRequest.abort();
            }

            // Show loading state
            this.showLoading();

            // Create AbortController for cancellation
            const controller = new AbortController();
            this.currentRequest = controller;

            try {
                let response;
                let data;

                // Try fast endpoint first (SHORTINIT)
                if (this.fastEndpoint) {
                    const params = new URLSearchParams({
                        s: query,
                        limit: this.resultsLimit,
                        cat_limit: this._maxCategoryLimit(),
                        _: Date.now()  // Cache buster to prevent server-side caching
                    });

                    try {
                        const separator = this.fastEndpoint.includes('?') ? '&' : '?';
                        response = await fetch(`${this.fastEndpoint}${separator}${params}`, {
                            signal: controller.signal
                        });
                        if (!response.ok) {
                            throw new Error(`HTTP ${response.status}`);
                        }
                        data = await response.json();


                    } catch (fastError) {
                        // Fast endpoint failed or returned empty - try admin-ajax fallback
                        if (fastError.name === 'AbortError') throw fastError;

                        response = await fetch(storedashSearch.ajaxUrl, {
                            method: 'POST',
                            body: this.buildAjaxFormData(query),
                            signal: controller.signal
                        });
                        if (!response.ok) {
                            throw new Error(`HTTP ${response.status}`);
                        }
                        data = await response.json();
                    }
                } else {
                    // No fast endpoint - use standard admin-ajax.php
                    response = await fetch(storedashSearch.ajaxUrl, {
                        method: 'POST',
                        body: this.buildAjaxFormData(query),
                        signal: controller.signal
                    });
                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}`);
                    }
                    data = await response.json();
                }

                if (data.success) {
                    // Cache results (with TTL + LRU)
                    this.cacheSet(cacheKey, data.data);

                    // Display results
                    this.displayResults(data.data, query);
                } else {
                    this.showError(data.data || 'Search failed');
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    this.showError('Search failed. Please try again.');
                }
            } finally {
                this.hideLoading();
                this.currentRequest = null;
            }
        }

        displayResults(data, query) {
            // Handle new data format: {products: [], categories: [], total_products: N}
            const products = data.products || data || [];
            const categories = data.categories || [];
            const totalProducts = data.total_products || products.length;

            // Store for responsive category limit re-rendering
            this._lastResults = data;

            // Check if we have any results
            if (!products.length && !categories.length) {
                this.showNoResults(query);
                return;
            }

            let html = '';

            // Determine layout based on layout mode setting
            if (this.layoutMode === 'two-column') {
                // Flexible two-column layout with independent positioning
                html = this.renderFlexibleTwoColumnLayout(products, categories, query);
            } else if (this.categoryPosition === 'left' || this.categoryPosition === 'right') {
                // Legacy two-column layout (for backwards compatibility)
                html = this.renderTwoColumnLayout(products, categories, query);
            } else {
                // Single column layout (above/below)
                if (this.categoryPosition === 'above' && categories.length > 0) {
                    html += this.renderCategorySection(categories, query);
                }

                html += this.renderProductSection(products, query);

                if (this.categoryPosition === 'below' && categories.length > 0) {
                    html += this.renderCategorySection(categories, query);
                }
            }

            // Add "View All Results" button if there are more results and URL is set
            if (this.searchResultsUrl && totalProducts > products.length) {
                html += this.renderViewAllButton(totalProducts);
            }

            // Mark suggestion index dirty so new titles get picked up
            this.suggestionIndexDirty = true;

            requestAnimationFrame(() => {
                this.results.innerHTML = html;
                this.activeIndex = -1;

                // Add click listeners to all result items (products + categories)
                this.attachResultListeners();

                // Add click listener to "View All" button
                this.attachViewAllListener();

                // Show results after DOM is ready
                this.showResults();

                // Announce results to screen readers
                this.announceResults(products.length + categories.length);
            });
        }

        attachResultListeners() {
            // No-op: delegated listener on this.results handles all clicks (set up in constructor)
        }

        attachViewAllListener() {
            const viewAllBtn = this.results.querySelector('.search-view-all-btn');
            if (viewAllBtn) {
                viewAllBtn.addEventListener('click', () => {
                    this.addToHistory(this.currentQuery);
                    this.redirectToSearchResults();
                });
            }
        }

        renderTwoColumnLayout(products, categories, query) {
            const leftContent = this.categoryPosition === 'left'
                ? this.renderCategorySection(categories, query)
                : this.renderProductSection(products, query);

            const rightContent = this.categoryPosition === 'left'
                ? this.renderProductSection(products, query)
                : this.renderCategorySection(categories, query);

            return `
                <div class="search-results-two-column">
                    <div class="search-results-column">${leftContent}</div>
                    <div class="search-results-column">${rightContent}</div>
                </div>
            `;
        }

        /**
         * Render flexible two-column layout with independent positioning
         * Categories and products can be placed in any position:
         * - left-top, left-bottom, right-top, right-bottom
         */
        renderFlexibleTwoColumnLayout(products, categories, query) {
            const hasCategories = this.showCategories && categories && categories.length > 0;
            const hasProducts = products && products.length > 0;

            // If no categories, just render products in single column
            if (!hasCategories) {
                return `
                    <div class="search-results-flexible-grid single-section">
                        ${this.renderProductSection(products, query)}
                    </div>
                `;
            }

            // Determine positions
            const catPos = `${this.categoriesHorizontal}-${this.categoriesVertical}`;
            const prodPos = `${this.productsHorizontal}-${this.productsVertical}`;

            // Check if both sections are in the same column
            const sameColumn = this.categoriesHorizontal === this.productsHorizontal;

            if (sameColumn) {
                // Same column - stack vertically based on vertical position
                const column = this.categoriesHorizontal;
                const catFirst = this.categoriesVertical === 'top' ||
                    (this.categoriesVertical === this.productsVertical && this.categoriesVertical === 'top');

                const firstSection = catFirst
                    ? this.renderCategorySection(categories, query)
                    : this.renderProductSection(products, query);
                const secondSection = catFirst
                    ? this.renderProductSection(products, query)
                    : this.renderCategorySection(categories, query);

                // Single column layout when both are in the same column
                return `
                    <div class="search-results-flexible-grid same-column column-${column}">
                        <div class="search-results-column search-results-column-stacked">
                            ${firstSection}
                            ${secondSection}
                        </div>
                    </div>
                `;
            }

            // Different columns - render as two-column grid
            const categorySection = this.renderCategorySection(categories, query);
            const productSection = this.renderProductSection(products, query);

            // Determine which content goes in left vs right column
            const leftContent = this.categoriesHorizontal === 'left' ? categorySection : productSection;
            const rightContent = this.categoriesHorizontal === 'left' ? productSection : categorySection;

            // Determine vertical alignment classes
            const leftVertical = this.categoriesHorizontal === 'left' ? this.categoriesVertical : this.productsVertical;
            const rightVertical = this.categoriesHorizontal === 'right' ? this.categoriesVertical : this.productsVertical;

            return `
                <div class="search-results-flexible-grid two-columns">
                    <div class="search-results-column search-results-column-left align-${leftVertical}">
                        ${leftContent}
                    </div>
                    <div class="search-results-column search-results-column-right align-${rightVertical}">
                        ${rightContent}
                    </div>
                </div>
            `;
        }

        renderProductSection(products, query) {
            if (!products || products.length === 0) {
                return '';
            }

            const html = products.map((product, index) => {
                return this.renderProduct(product, index);
            }).join('');

            return `
                <div class="search-section search-section-products">
                    <div class="search-section-header">${this.escapeHtml(this.productsLabel)}</div>
                    ${html}
                </div>
            `;
        }

        renderCategorySection(categories, query) {
            if (!categories || categories.length === 0 || !this.showCategories) {
                return '';
            }

            // Store full list for responsive re-rendering
            this._lastCategories = categories;

            const html = categories.map((category, index) => {
                const hidden = index >= this.categoryLimit ? ' style="display:none"' : '';
                return this.renderCategory(category, index, hidden);
            }).join('');

            return `
                <div class="search-section search-section-categories">
                    <div class="search-section-header">${this.escapeHtml(this.categoriesLabel)}</div>
                    ${html}
                </div>
            `;
        }

        renderCategory(category, index, extraAttrs = '') {
            let html = `<div class="search-result-item search-result-category" role="option" data-index="${index}" data-url="${this.escapeHtml(category.url)}"${extraAttrs}>`;
            
            if (this.categoryShowImage && category.image) {
                html += `
                    <div class="search-result-image">
                        <img src="${this.escapeHtml(category.image)}" alt="${this.escapeHtml(category.name)}" loading="lazy" />
                    </div>
                `;
            }
            
            html += `<div class="search-result-details">`;
            html += `<div class="search-result-title">${this.highlightText(category.name, this.input.value)}</div>`;
            
            if (this.categoryShowCount && category.product_count !== undefined) {
                html += `<div class="search-result-meta">${category.product_count} products</div>`;
            }
            
            html += `</div></div>`;
            
            return html;
        }

        renderProduct(product, index) {
            let html = `<div class="search-result-item" role="option" data-index="${index}" data-url="${this.escapeHtml(product.url)}">`;

            if (this.showImages && product.image) {
                html += `
                    <div class="search-result-image">
                        <img src="${this.escapeHtml(product.image)}" alt="${this.escapeHtml(product.title)}" loading="lazy" />
                    </div>
                `;
            }

            html += `<div class="search-result-details">`;
            html += `<div class="search-result-title">${this.highlightText(product.title, this.input.value)}</div>`;

            const meta = [];
            if (this.showSku && product.sku) {
                meta.push(`<span class="search-result-sku">SKU: ${this.escapeHtml(product.sku)}</span>`);
            }
            if (this.showCategory && product.category) {
                meta.push(`<span class="search-result-category">${this.escapeHtml(product.category)}</span>`);
            }

            if (meta.length > 0) {
                html += `<div class="search-result-meta">${meta.join(' • ')}</div>`;
            }

            if (this.showPrice && product.price) {
                html += `<div class="search-result-price">${product.price}</div>`;
            }

            html += `</div></div>`;

            return html;
        }

        renderViewAllButton(totalResults) {
            const stickyClass = this.viewAllSticky ? ' is-sticky' : '';
            return `
                <div class="search-view-all-wrapper${stickyClass}">
                    <button type="button" class="search-view-all-btn">
                        ${this.escapeHtml(this.viewAllText)} (${totalResults})
                    </button>
                </div>
            `;
        }

        redirectToSearchResults() {
            if (!this.searchResultsUrl || !this.currentQuery) return;

            // Build URL with search query parameter
            const url = new URL(this.searchResultsUrl);
            url.searchParams.set('s', this.currentQuery);
            window.location.href = url.toString();
        }

        highlightText(text, query) {
            if (!query) return this.escapeHtml(text);
            
            const escapedText = this.escapeHtml(text);
            const terms = query.trim().split(/\s+/);
            let highlighted = escapedText;
            
            terms.forEach(term => {
                if (term.length > 0) {
                    const regex = new RegExp(`(${this.escapeRegex(term)})`, 'gi');
                    highlighted = highlighted.replace(regex, '<mark>$1</mark>');
                }
            });
            
            return highlighted;
        }

        showNoResults(query) {
            this.results.innerHTML = `
                <div class="search-no-results">
                    <p>No results found for "<strong>${this.escapeHtml(query)}</strong>"</p>
                </div>
            `;
            this.showResults();
            this.announceResults(0);
        }

        showError(message) {
            this.results.innerHTML = `
                <div class="search-error">
                    <p>${this.escapeHtml(message)}</p>
                </div>
            `;
            this.showResults();
        }

        handleKeydown(e) {
            // Ghost text acceptance: Tab or Right Arrow at end of input
            if ((e.key === 'Tab' || e.key === 'ArrowRight') && this.currentSuggestion) {
                const val = this.input.value;
                if (this.input.selectionStart === val.length && this.input.selectionEnd === val.length) {
                    e.preventDefault();
                    this.acceptGhostText();
                    return;
                }
            }

            const items = this.results.querySelectorAll('.search-result-item');

            if (items.length === 0 && e.key !== 'Enter') return;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    this.activeIndex = Math.min(this.activeIndex + 1, items.length - 1);
                    this.updateActiveItem(items);
                    break;

                case 'ArrowUp':
                    e.preventDefault();
                    this.activeIndex = Math.max(this.activeIndex - 1, -1);
                    this.updateActiveItem(items);
                    break;

                case 'Enter':
                    e.preventDefault();
                    // If an item is selected (via arrow keys), go to that product
                    if (this.activeIndex >= 0 && items[this.activeIndex]) {
                        const selected = items[this.activeIndex];
                        if (selected.classList.contains('search-history-item')) {
                            const historyQuery = this.normalizeHistoryQuery(selected.dataset.query || '');
                            if (historyQuery) {
                                this.input.value = historyQuery;
                                this.currentQuery = historyQuery;
                                this.clearBtn.style.display = 'block';
                                this.isFirstQuery = false;
                                this.performSearch(historyQuery);
                            }
                            return;
                        }

                        if (selected.dataset.url) {
                            this.addToHistory(this.currentQuery);
                            window.location.href = selected.dataset.url;
                        }
                    }
                    // Otherwise, redirect to search results page if URL is set
                    else if (this.searchResultsUrl && this.currentQuery) {
                        this.addToHistory(this.currentQuery);
                        this.redirectToSearchResults();
                    }
                    // Fallback: go to first result if no search results URL
                    else if (items.length > 0) {
                        const firstItem = items[0];
                        if (firstItem.classList.contains('search-history-item')) {
                            const historyQuery = this.normalizeHistoryQuery(firstItem.dataset.query || '');
                            if (historyQuery) {
                                this.input.value = historyQuery;
                                this.currentQuery = historyQuery;
                                this.clearBtn.style.display = 'block';
                                this.isFirstQuery = false;
                                this.performSearch(historyQuery);
                            }
                        } else if (firstItem.dataset.url) {
                            this.addToHistory(this.currentQuery);
                            window.location.href = firstItem.dataset.url;
                        }
                    }
                    break;

                case 'Escape':
                    e.preventDefault();
                    this.hideResults();
                    this.input.blur();
                    break;
            }
        }

        updateActiveItem(items) {
            // Batch class updates (reads separated from writes to avoid layout thrashing)
            items.forEach((item, index) => {
                item.classList.toggle('active', index === this.activeIndex);
            });

            // Single scrollIntoView after all class changes
            const activeItem = items[this.activeIndex];
            if (activeItem) {
                activeItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

                // Update aria-activedescendant for screen readers
                if (!activeItem.id) {
                    activeItem.id = `${this.element.dataset.widgetId}-result-${this.activeIndex}`;
                }
                this.input.setAttribute('aria-activedescendant', activeItem.id);
            } else {
                this.input.removeAttribute('aria-activedescendant');
            }
        }

        handleFocus() {
            const query = this.input.value.trim();
            if (query.length < this.minChars) {
                this.showHistorySuggestions();
                return;
            }

            if (query.length >= this.minChars && this.results.innerHTML) {
                this.showResults();
            }
        }

        handleSubmit(e) {
            e.preventDefault();
            // Redirect to search results page if URL is set
            if (this.searchResultsUrl && this.currentQuery) {
                this.addToHistory(this.currentQuery);
                this.redirectToSearchResults();
            } else {
                // Fallback: go to first result
                const items = this.results.querySelectorAll('.search-result-item');
                if (items.length > 0) {
                    if (items[0].dataset.url) {
                        this.addToHistory(this.currentQuery);
                        window.location.href = items[0].dataset.url;
                    }
                }
            }
        }

        clearSearch() {
            this.input.value = '';
            this.clearBtn.style.display = 'none';
            this.clearGhostText();
            this.hideResults();
            this.input.focus();
            this.showHistorySuggestions();
        }

        showResults() {
            this.results.style.display = 'block';
        }

        hideResults() {
            this.results.style.display = 'none';
            this.activeIndex = -1;
            this.clearGhostText();
        }

        showLoading() {
            // Use opacity and visibility to prevent reflow/repaint
            this.searchIcon.style.opacity = '0';
            this.searchIcon.style.visibility = 'hidden';
            this.loadingIcon.style.opacity = '1';
            this.loadingIcon.style.visibility = 'visible';
            this.loadingIcon.classList.add('spin');
        }

        hideLoading() {
            // Use opacity and visibility to prevent reflow/repaint
            this.searchIcon.style.opacity = '1';
            this.searchIcon.style.visibility = 'visible';
            this.loadingIcon.style.opacity = '0';
            this.loadingIcon.style.visibility = 'hidden';
            this.loadingIcon.classList.remove('spin');
        }

        /**
         * Announce search results to screen readers via aria-live region
         */
        announceResults(count) {
            const announcer = this.element.querySelector('.storedash-sr-announcer');
            if (announcer) {
                announcer.textContent = count > 0
                    ? `${count} result${count !== 1 ? 's' : ''} found`
                    : 'No results found';
            }
        }

        announceHistoryCount(count) {
            const announcer = this.element.querySelector('.storedash-sr-announcer');
            if (announcer) {
                announcer.textContent = count > 0
                    ? `${count} recent search${count !== 1 ? 'es' : ''}`
                    : this.searchHistoryEmptyText;
            }
        }

        /**
         * Enable focus trap for mobile fullscreen overlay
         */
        enableFocusTrap() {
            const wrapper = this.mobileWrapper;
            if (!wrapper) return;

            this._focusTrapHandler = (e) => {
                if (e.key !== 'Tab') return;

                const focusableEls = wrapper.querySelectorAll(
                    'input, button, [href], [tabindex]:not([tabindex="-1"]), .search-result-item'
                );
                if (focusableEls.length === 0) return;

                const firstEl = focusableEls[0];
                const lastEl = focusableEls[focusableEls.length - 1];

                if (e.shiftKey && document.activeElement === firstEl) {
                    e.preventDefault();
                    lastEl.focus();
                } else if (!e.shiftKey && document.activeElement === lastEl) {
                    e.preventDefault();
                    firstEl.focus();
                }
            };
            wrapper.addEventListener('keydown', this._focusTrapHandler);
        }

        /**
         * Disable focus trap
         */
        disableFocusTrap() {
            if (this._focusTrapHandler && this.mobileWrapper) {
                this.mobileWrapper.removeEventListener('keydown', this._focusTrapHandler);
                this._focusTrapHandler = null;
            }
        }

        // ============================================
        // Ghost Text (Inline Autocomplete)
        // ============================================

        initGhostText() {
            this.ghostTextEl = this.element.querySelector('.storedash-ghost-text');
            if (!this.ghostTextEl) return;

            // Seed suggestion index from search history
            this.suggestionIndexDirty = true;

            // CJK composition suppression (stored for cleanup in destroyGhostText)
            this._boundCompositionStart = () => { this.isComposing = true; this.clearGhostText(); };
            this._boundCompositionEnd = () => { this.isComposing = false; this.updateGhostText(); };
            this.input.addEventListener('compositionstart', this._boundCompositionStart);
            this.input.addEventListener('compositionend', this._boundCompositionEnd);

            // Mobile: tap on ghost area accepts suggestion
            this._boundGhostTouchEnd = (e) => {
                if (this.currentSuggestion) {
                    e.preventDefault();
                    this.acceptGhostText();
                }
            };
            this.ghostTextEl.addEventListener('touchend', this._boundGhostTouchEnd);
        }

        destroyGhostText() {
            if (this._boundCompositionStart) {
                this.input.removeEventListener('compositionstart', this._boundCompositionStart);
                this._boundCompositionStart = null;
            }
            if (this._boundCompositionEnd) {
                this.input.removeEventListener('compositionend', this._boundCompositionEnd);
                this._boundCompositionEnd = null;
            }
            if (this._boundGhostTouchEnd && this.ghostTextEl) {
                this.ghostTextEl.removeEventListener('touchend', this._boundGhostTouchEnd);
                this._boundGhostTouchEnd = null;
            }
        }

        rebuildSuggestionIndex() {
            if (!this.suggestionIndexDirty) return;
            const set = new Set();

            // Add search history
            const history = this.loadHistory();
            history.forEach(h => set.add(h));

            // Add cached product titles and category names
            this.cache.forEach((entry) => {
                const data = entry.data || entry;
                const products = data.products || data || [];
                if (Array.isArray(products)) {
                    products.forEach(p => { if (p.title) set.add(this.stripHtml(p.title)); });
                }
                const categories = data.categories || [];
                if (Array.isArray(categories)) {
                    categories.forEach(c => { if (c.name) set.add(c.name); });
                }
            });

            // Sort for binary search
            this.suggestionIndex = Array.from(set).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
            this.suggestionIndexDirty = false;
        }

        stripHtml(str) {
            return str.replace(/<[^>]*>/g, '');
        }

        findBestSuggestion(typed) {
            if (!typed) return '';
            const lower = typed.toLowerCase();

            this.rebuildSuggestionIndex();

            // Priority 1: History prefix match (most recent first)
            const history = this.loadHistory();
            for (const h of history) {
                if (h.toLowerCase().startsWith(lower) && h.toLowerCase() !== lower) {
                    return h;
                }
            }

            // Priority 2: Binary search on sorted suggestion index
            const idx = this.suggestionIndex;
            let lo = 0, hi = idx.length - 1;
            while (lo <= hi) {
                const mid = (lo + hi) >>> 1;
                const cmp = idx[mid].toLowerCase().localeCompare(lower);
                if (cmp < 0) lo = mid + 1;
                else hi = mid - 1;
            }

            // lo is now the first index >= typed (case-insensitive)
            for (let i = lo; i < idx.length; i++) {
                const candidate = idx[i];
                if (!candidate.toLowerCase().startsWith(lower)) break;
                if (candidate.toLowerCase() !== lower) return candidate;
            }

            return '';
        }

        updateGhostText() {
            if (!this.ghostTextEl || this.isComposing) return;

            const typed = this.input.value;
            // Only show ghost text when cursor is at end
            if (this.input.selectionStart !== typed.length || this.input.selectionEnd !== typed.length) {
                this.clearGhostText();
                return;
            }

            if (!typed) {
                this.clearGhostText();
                return;
            }

            const suggestion = this.findBestSuggestion(typed);
            if (!suggestion) {
                this.clearGhostText();
                return;
            }

            this.currentSuggestion = suggestion;
            const suffix = suggestion.slice(typed.length);

            // Render: invisible spacer matching typed text + visible gray suffix
            this.ghostTextEl.innerHTML =
                '<span class="storedash-ghost-typed">' + this.escapeHtml(typed) + '</span>' +
                '<span class="storedash-ghost-suffix">' + this.escapeHtml(suffix) + '</span>';
        }

        clearGhostText() {
            this.currentSuggestion = '';
            if (this.ghostTextEl) {
                this.ghostTextEl.innerHTML = '';
            }
        }

        acceptGhostText() {
            if (!this.currentSuggestion) return false;

            const suggestion = this.currentSuggestion;
            this.input.value = suggestion;
            this.currentQuery = suggestion;
            this.clearGhostText();
            this.clearBtn.style.display = 'block';

            // Trigger search if meets minimum character threshold
            if (suggestion.length >= this.minChars) {
                this.isFirstQuery = false;
                this.performSearch(suggestion);
            }

            return true;
        }

        escapeHtml(text) {
            // PERF FIX: String replacement instead of DOM element creation
            return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        }

        escapeRegex(text) {
            return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        }
    }

    // Initialize all search widgets
    function initSearchWidgets() {
        document.querySelectorAll('.storedash-product-search-widget').forEach(element => {
            if (!element.dataset.initialized) {
                new StoredashProductSearch(element);
                element.dataset.initialized = 'true';
            }
        });
    }

    // Initialize on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initSearchWidgets);
    } else {
        initSearchWidgets();
    }

    // Re-initialize on Elementor frontend init (for Elementor editor)
    if (window.elementorFrontend && window.elementorFrontend.hooks) {
        window.elementorFrontend.hooks.addAction('frontend/element_ready/storedash-product-search.default', function($scope) {
            const element = $scope[0].querySelector('.storedash-product-search-widget');
            if (element && !element.dataset.initialized) {
                new StoredashProductSearch(element);
                element.dataset.initialized = 'true';
            }
        });
    }

    /**
     * Bricks Builder editor integration
     * Called by Bricks via $scripts = ['storedashBricksSearch'] when the
     * element renders or re-renders in the editor.
     */
    if (typeof window.storedashBricksSearch === 'undefined') {
        window.storedashBricksSearch = function(element) {
            if (!element) return;
            const widget = element.querySelector('.storedash-product-search-widget');
            if (widget) {
                delete widget.dataset.initialized;
                new StoredashProductSearch(widget);
                widget.dataset.initialized = 'true';
            }
        };
    }

})();

